/* Scratch on hover for the map cards.
   <lm-scratch data-foil="url" data-mask="url" data-brush="55"> sits over the scratched card image.
   The foil is drawn on a canvas; moving the pointer erases it (destination-out). The canvas resizes with the card.
   Touch / coarse pointers: one automatic reveal sweep when the card scrolls into view, then it stays revealed.
   data-mode="interactive" (product page): hover scratches with a fine pointer, press-and-drag scratches with a finger
   or pen; after data-finish % of the foil is gone the rest fades (class is-done); a [data-lm-reset] button in the
   same block repaints the foil. Reduced motion: the element is hidden by CSS, nothing runs. */
class LmScratch extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d');
    this.appendChild(this.canvas);
    this.brush = Number(this.dataset.brush || 55);
    this.last = null;
    this.foil = new Image();
    this.foil.crossOrigin = 'anonymous';
    this.foil.onload = () => this.paint();
    this.foil.src = this.dataset.foil;
    if (this.dataset.mask) {
      this.mask = new Image();
      this.mask.crossOrigin = 'anonymous';
      this.mask.onload = () => this.paint();
      this.mask.src = this.dataset.mask;
    }
    this.ro = new ResizeObserver(() => this.paint());
    this.ro.observe(this);
    this.card = this.closest('.lm-card') || this;
    this.interactive = this.dataset.mode === 'interactive';
    this.finishAt = Number(this.dataset.finish || 55) / 100;
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (this.interactive) {
      // every pointer type: press to scratch; a fine pointer also scratches on plain hover
      this.card.addEventListener('pointerdown', this.onDown);
      this.card.addEventListener('pointermove', this.onMoveInteractive);
      this.card.addEventListener('pointerup', this.onUp);
      this.card.addEventListener('pointercancel', this.onUp);
      this.card.addEventListener('pointerleave', () => (this.last = null));
      const wrap = this.closest('.lm-scratchcard') || this.card;
      wrap.querySelectorAll('[data-lm-reset]').forEach((b) => b.addEventListener('click', () => this.reset()));
    } else if (fine) {
      this.card.addEventListener('pointermove', this.onMove);
      this.card.addEventListener('pointerleave', () => (this.last = null));
    } else {
      this.io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.io.disconnect();
          this.autoReveal();
        }
      }, { threshold: 0.6 });
      this.io.observe(this);
    }
  }
  disconnectedCallback() {
    this.ro && this.ro.disconnect();
    this.io && this.io.disconnect();
    this.card && this.card.removeEventListener('pointermove', this.onMove);
  }
  paint() {
    if (!this.foil.complete || !this.foil.naturalWidth) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, Math.round(this.clientWidth * dpr));
    const h = Math.max(1, Math.round(this.clientHeight * dpr));
    const maskReady = !!(this.mask && this.mask.complete && this.mask.naturalWidth);
    if (this.canvas.width === w && this.canvas.height === h && this.painted && this.maskApplied === maskReady) return;
    this.canvas.width = w;
    this.canvas.height = h;
    const ctx = this.ctx;
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(this.foil, 0, 0, w, h);
    if (maskReady) {
      ctx.globalCompositeOperation = 'destination-in';
      ctx.drawImage(this.mask, 0, 0, w, h);
    }
    this.maskApplied = maskReady;
    ctx.globalCompositeOperation = 'destination-out';
    this.painted = true;
    this.strokes = 0;
    if (this.interactive) {
      // remember which sample points sit on the foil at all, so coverage counts the map area only
      const img = ctx.getImageData(0, 0, w, h).data, n = 48;
      this.maskOpaque = new Uint8Array(n * n);
      for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) {
        const x = Math.floor((i + 0.5) * w / n), y = Math.floor((j + 0.5) * h / n);
        this.maskOpaque[j * n + i] = img[(y * w + x) * 4 + 3] > 40 ? 1 : 0;
      }
    }
  }
  onDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    this.down = true;
    this.last = null;
    try { this.card.setPointerCapture(e.pointerId); } catch (_) {}
    this.onMove(e);
  };
  onUp = () => { this.down = false; this.last = null; };
  onMoveInteractive = (e) => {
    if (this.down || e.pointerType === 'mouse') this.onMove(e);
  };
  reset() {
    this.painted = false;
    this.done = false;
    this.classList.remove('is-done');
    this.canvas.style.opacity = '';
    this.paint();
  }
  checkCoverage() {
    // sample a 48×48 grid of alpha values; cheap enough to run every few strokes
    if (this.done || !this.interactive) return;
    const { width: w, height: h } = this.canvas;
    const img = this.ctx.getImageData(0, 0, w, h).data;
    const n = 48;
    let covered = 0, total = 0;
    for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) {
      const x = Math.floor((i + 0.5) * w / n), y = Math.floor((j + 0.5) * h / n);
      const a = img[(y * w + x) * 4 + 3];
      if (this.maskOpaque && !this.maskOpaque[j * n + i]) continue;
      total++;
      if (a > 40) covered++;
    }
    if (total && 1 - covered / total >= this.finishAt) {
      this.done = true;
      this.classList.add('is-done');
    }
  }
  onMove = (e) => {
    const r = this.getBoundingClientRect();
    const dpr = this.canvas.width / r.width;
    const x = (e.clientX - r.left) * dpr;
    const y = (e.clientY - r.top) * dpr;
    this.scratchAt(x, y, dpr);
  };
  scratchAt(x, y, dpr) {
    const ctx = this.ctx;
    const b = this.brush * dpr;
    ctx.globalCompositeOperation = 'destination-out';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = b;
    ctx.beginPath();
    if (this.last) ctx.moveTo(this.last.x, this.last.y);
    else ctx.moveTo(x, y);
    ctx.lineTo(x, y);
    ctx.stroke();
    this.last = { x, y };
    if (this.interactive && (++this.strokes % 6 === 0)) this.checkCoverage();
  }
  autoReveal() {
    // one diagonal sweep of strokes, like a coin passing over the map, ~1.2 s
    const w = this.canvas.width, h = this.canvas.height;
    const dpr = w / Math.max(1, this.clientWidth);
    const rows = 7;
    let i = 0;
    const start = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - start) / 1200);
      const target = Math.floor(p * rows * 40);
      while (i < target) {
        const row = Math.floor(i / 40);
        const k = (i % 40) / 39;
        const y = h * (0.1 + 0.8 * row / (rows - 1)) + Math.sin(k * 9) * h * 0.02;
        const x = (row % 2 ? 1 - k : k) * w;
        if (i % 40 === 0) this.last = null;
        this.scratchAt(x, y, dpr);
        i++;
      }
      if (p < 1) requestAnimationFrame(step);
      else this.canvas.style.transition = 'opacity 600ms ease-out', (this.canvas.style.opacity = '0');
    };
    requestAnimationFrame(step);
  }
}
if (!customElements.get('lm-scratch')) customElements.define('lm-scratch', LmScratch);
